import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendorProfileApi, profileApi, vendorsApi } from '../api/client';

// First-run seller onboarding: before a vendor can use the app they build a
// capability profile from up to THREE sources combined — a free-text description,
// their SAM.gov registration (looked up by name/UEI/CAGE), and their company website.
// The AI extracts one editable profile; saving it unblocks the seller experience.

const CHIP_FIELDS: { key: string; label: string; hint: string }[] = [
  { key: 'naics_titles', label: 'Industry titles (NAICS)', hint: 'e.g. Computer Systems Design Services' },
  { key: 'core_expertise', label: 'Core expertise', hint: 'e.g. cybersecurity engineering' },
  { key: 'products_services', label: 'Products & services', hint: 'e.g. incident response' },
  { key: 'keywords', label: 'Keywords', hint: 'short terms a matcher should use' },
  { key: 'domains', label: 'Domains / agencies', hint: 'e.g. DoD, DHS' },
  { key: 'technologies', label: 'Technologies', hint: 'e.g. AWS, Kubernetes' },
  { key: 'differentiators', label: 'Differentiators', hint: 'e.g. FedRAMP High' },
];

interface Props { onComplete?: () => void; }

export default function SellerOnboarding({ onComplete }: Props) {
  const navigate = useNavigate();

  // Sources
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [samQuery, setSamQuery] = useState('');
  const [samMatches, setSamMatches] = useState<any[] | null>(null);
  const [samPicked, setSamPicked] = useState<any | null>(null);
  const [samSearching, setSamSearching] = useState(false);
  const [autoSearched, setAutoSearched] = useState(false);

  const [profile, setProfile] = useState<any>(null);
  const [summary, setSummary] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [hasSavedProfile, setHasSavedProfile] = useState(false);

  // Load the seller's SAVED capability profile on return so it persists (not a blank
  // builder every time). If one exists, we show it for review/editing.
  useEffect(() => {
    (async () => {
      try {
        const res = await vendorProfileApi.get();
        const cp = res?.capability_profile;
        if (cp && Object.keys(cp).length) {
          setProfile(cp);
          setSummary(cp.summary || '');
          setSavedAt(res.updated_at ? Number(res.updated_at) : null);
          setHasSavedProfile(true);
        }
      } catch { /* no saved profile yet — fine, show the builder */ }
    })();
  }, []);

  const searchSam = useCallback(async (queryOverride?: string, silent = false) => {
    const q = (queryOverride ?? samQuery).trim();
    if (!silent) setError('');
    setSamMatches(null);
    if (q.length < 3) { if (!silent) setError('Enter at least 3 characters to search SAM.gov.'); return; }
    setSamSearching(true);
    try {
      // Use the SAME search as the Vendors tab (GET /vendors?search=) so results and
      // fields match exactly. Normalize each row into the picker shape.
      const res = await vendorsApi.list({ search: q, page: '1', page_size: '8' });
      const rows = Array.isArray(res?.items) ? res.items : [];
      setSamMatches(rows.slice(0, 8).map((it: any) => ({
        vendor_id: it.vendor_id || it.id,
        name: it.name || '',
        uei: it.uei || '',
        cage_code: it.cage_code || '',
        city: it.city || '', state: it.state || '',
        primary_naics: it.primary_naics || '',
        award_count: it.award_count || 0,
      })));
    } catch (err: any) {
      if (!silent) setError(err?.message || 'Vendor search failed.');
    }
    setSamSearching(false);
  }, [samQuery]);

  // On first load, seed the SAM search from the seller's display name (their most
  // reliable company signal) and auto-run the lookup so step 2 is pre-filled.
  const autoSearchedRef = useRef(false);
  useEffect(() => {
    if (autoSearchedRef.current) return;
    autoSearchedRef.current = true;
    (async () => {
      try {
        const res = await profileApi.get();
        const name = (res?.profile?.display_name || '').trim();
        // Only auto-search if it looks like a company name (not an email or blank).
        if (name.length >= 3 && !name.includes('@')) {
          setSamQuery(name);
          setAutoSearched(true);
          await searchSam(name, true);
        }
      } catch { /* no profile / not signed in yet — user can search manually */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const build = async () => {
    setError(''); setNotice('');
    const hasSource = text.trim() || url.trim() || samPicked;
    if (!hasSource) { setError('Add at least one source: a description, your website, or your SAM record.'); return; }
    setExtracting(true);
    try {
      const res = await vendorProfileApi.extract({
        text: text.trim() || undefined,
        url: url.trim() || undefined,
        vendor_id: samPicked?.vendor_id || undefined,
      });
      const p = res?.profile || {};
      setProfile(p);
      setSummary(p.summary || '');
      const srcs = (res?.sources || []).join(' + ');
      setNotice(`Draft profile built${srcs ? ' from ' + srcs : ''}. Review and edit, then save to continue.`);
    } catch (err: any) {
      setError(err?.message || 'Could not build a profile from those sources.');
    }
    setExtracting(false);
  };

  const updateList = (key: string, next: string[]) =>
    setProfile((prev: any) => ({ ...(prev || {}), [key]: next }));
  const addChip = (key: string, value: string) => {
    const v = value.trim(); if (!v) return;
    const cur: string[] = Array.isArray(profile?.[key]) ? profile[key] : [];
    if (cur.some(x => x.toLowerCase() === v.toLowerCase())) return;
    updateList(key, [...cur, v]);
  };
  const removeChip = (key: string, idx: number) => {
    const cur: string[] = Array.isArray(profile?.[key]) ? profile[key] : [];
    updateList(key, cur.filter((_, i) => i !== idx));
  };

  // Persist the profile without leaving the page (for editing a saved profile).
  const saveProfile = async () => {
    setError(''); setNotice('');
    if (!profile) { setError('Build a profile first.'); return; }
    setSaving(true);
    try {
      await vendorProfileApi.save({ ...profile, summary });
      onComplete?.();
      setSavedAt(Math.floor(Date.now() / 1000));
      setHasSavedProfile(true);
      setNotice('Capabilities saved.');
    } catch (err: any) {
      setError(err?.message || 'Save failed.');
    }
    setSaving(false);
  };

  const saveAndContinue = async () => {
    setError(''); setNotice('');
    if (!profile) { setError('Build a profile first.'); return; }
    setSaving(true);
    try {
      await vendorProfileApi.save({ ...profile, summary });
      onComplete?.();
      navigate('/match-me');
    } catch (err: any) {
      setError(err?.message || 'Save failed.');
      setSaving(false);
    }
  };

  const hasProfile = profile && CHIP_FIELDS.some(f => Array.isArray(profile[f.key]) && profile[f.key].length);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#6366f1' }}>{hasSavedProfile ? 'My Capabilities' : 'Welcome'}</div>
        <h1 style={{ fontSize: 24, margin: '4px 0 6px', color: '#0f172a' }}>{hasSavedProfile ? 'Your capability profile' : 'Set up your capability profile'}</h1>
        <p style={{ color: '#475569', fontSize: 14, margin: 0 }}>
          {hasSavedProfile
            ? <>This is your saved profile — we use it to match you to federal opportunities. Edit it below and <strong>Save</strong> anytime, or add another source to refresh it.</>
            : <>We use this to match you to the right federal opportunities. Give us <strong>any or all</strong> of the three sources below — the more you provide, the better the matches.</>}
        </p>
      </div>

      {/* Source 1 — description */}
      <section style={card}>
        <SectionHead n={1} title="Describe your company" sub="What you do, core services, technologies, agencies you serve, past performance, certifications." />
        <textarea
          value={text} onChange={e => setText(e.target.value)} rows={5}
          placeholder="e.g. We provide cybersecurity engineering and cloud modernization for DoD and civilian agencies; FedRAMP High experience; incident response; Zero Trust…"
          style={input}
        />
      </section>

      {/* Source 2 — SAM.gov */}
      <section style={card}>
        <SectionHead n={2} title="Pull your SAM.gov registration"
          sub={autoSearched
            ? 'We pre-filled your company name — pick your entity below, or edit the search.'
            : 'Same search as the Vendors tab — by company name, UEI, or CAGE. Imports your NAICS/PSC and award history.'} />
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={samQuery} onChange={e => setSamQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchSam(); } }}
            placeholder="Type vendor name (e.g. Deloitte, Palantir, Carahsoft)..." style={{ ...input, marginTop: 0 }}
          />
          <button onClick={() => searchSam()} disabled={samSearching} style={btn(samSearching, '#0f766e')}>
            {samSearching ? 'Searching…' : 'Search'}
          </button>
        </div>
        {samMatches && samMatches.length === 0 && (
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>No matches. Check the spelling or use your UEI/CAGE — or skip this source.</div>
        )}
        {samMatches && samMatches.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {samMatches.map((m) => {
              const picked = samPicked?.vendor_id === m.vendor_id;
              return (
                <button key={m.vendor_id} onClick={() => setSamPicked(picked ? null : m)}
                  style={{ textAlign: 'left', padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${picked ? '#0f766e' : '#e2e8f0'}`, background: picked ? '#ecfdf5' : 'white' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{m.name || 'Unnamed entity'}{picked ? '  ✓' : ''}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    {[m.uei && `UEI ${m.uei}`, m.cage_code && `CAGE ${m.cage_code}`, (m.city || m.state) && `${m.city || ''} ${m.state || ''}`.trim(), m.award_count ? `${m.award_count} awards` : ''].filter(Boolean).join(' · ')}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {samPicked && <div style={{ fontSize: 12, color: '#0f766e', marginTop: 8 }}>Using <strong>{samPicked.name}</strong>. Click again to deselect.</div>}
      </section>

      {/* Source 3 — website */}
      <section style={card}>
        <SectionHead n={3} title="Add your company website" sub="We'll scan your site for capabilities. Optional." />
        <input value={url} onChange={e => setUrl(e.target.value)} placeholder="yourcompany.com" style={input} />
      </section>

      <div style={{ marginTop: 16 }}>
        <button onClick={build} disabled={extracting} style={btn(extracting)}>
          {extracting ? 'Building your profile…' : 'Build my profile'}
        </button>
      </div>

      {error && <div role="alert" style={alert('#991b1b', '#fee2e2')}>{error}</div>}
      {notice && !error && <div style={alert('#166534', '#dcfce7')}>{notice}</div>}

      {/* Review & edit */}
      {hasProfile && (
        <section style={card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, color: '#0f172a' }}>{hasSavedProfile ? 'Your capability profile' : 'Review your profile'}</h3>
            {savedAt ? <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>saved {new Date(savedAt * 1000).toLocaleString()}</span> : null}
          </div>
          <label style={lbl}>Summary</label>
          <textarea value={summary} onChange={e => setSummary(e.target.value)} rows={2} style={{ ...input, marginBottom: 8 }} />
          {CHIP_FIELDS.map(f => (
            <ChipEditor key={f.key} label={f.label} hint={f.hint}
              values={Array.isArray(profile[f.key]) ? profile[f.key] : []}
              onAdd={v => addChip(f.key, v)} onRemove={i => removeChip(f.key, i)} />
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button onClick={saveProfile} disabled={saving} style={btn(saving)}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={saveAndContinue} disabled={saving} style={btn(saving, '#0f766e')}>
              {saving ? 'Saving…' : 'Save & find matches →'}
            </button>
          </div>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
            You can refine this anytime later from Match Me. Everything here is advisory.
          </p>
        </section>
      )}
    </div>
  );
}

// ── presentational helpers ──
const card: React.CSSProperties = { border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, background: 'white', marginTop: 16 };
const input: React.CSSProperties = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginTop: 8 };
const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 };
const alert = (fg: string, bg: string): React.CSSProperties => ({ color: fg, background: bg, padding: '10px 14px', borderRadius: 8, marginTop: 16, fontSize: 13 });
const btn = (busy: boolean, color = '#4338ca'): React.CSSProperties => ({ padding: '10px 20px', background: busy ? '#94a3b8' : color, color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: busy ? 'default' : 'pointer', whiteSpace: 'nowrap' });

function SectionHead({ n, title, sub }: { n: number; title: string; sub: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
      <span style={{ flexShrink: 0, width: 24, height: 24, borderRadius: 999, background: '#eef2ff', color: '#4338ca', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</span>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{title}</div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{sub}</div>
      </div>
    </div>
  );
}

function ChipEditor({ label, hint, values, onAdd, onRemove }:
  { label: string; hint: string; values: string[]; onAdd: (v: string) => void; onRemove: (i: number) => void }) {
  const [draft, setDraft] = useState('');
  const commit = () => { onAdd(draft); setDraft(''); };
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {values.map((v, i) => (
          <span key={i} style={{ fontSize: 12, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999, padding: '4px 8px 4px 10px', color: '#334155', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {v}
            <button onClick={() => onRemove(i)} aria-label={`Remove ${v}`} style={{ border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
          </span>
        ))}
        <input value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }} onBlur={commit}
          placeholder={values.length ? 'add…' : hint}
          style={{ flex: '1 1 140px', minWidth: 120, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12 }} />
      </div>
    </div>
  );
}
